import type { CreditPackage } from '@frontend/api-types';
import { formatCurrencyNatural, formatNumber, makeCent, pluralize } from '@frontend/duck-tape';
import { getCreditsTaskEstimate } from '@frontend/react-domain';
import { Icon, Text, XStack, YStack } from '@frontend/web-react';
import { usePricingTerminology } from '@frontend/web/context';

export type CreditPackagesListProps = {
  minimumCredits?: number;
  onSelectPackage: (pkg: CreditPackage) => void;
  packages: CreditPackage[];
  selectedPackage?: CreditPackage;
};

export const CreditPackagesList = ({
  minimumCredits,
  onSelectPackage,
  packages,
  selectedPackage,
}: CreditPackagesListProps) => {
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const unitTerm = pricingTerminology.unit_term;
  const handleKeyDown = (e: React.KeyboardEvent, pkg: CreditPackage) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelectPackage(pkg);
    }
  };

  return (
    <YStack gapY="md" role="radiogroup">
      {packages.map((pkg) => {
        const isSelected = selectedPackage?.amount === pkg.amount && selectedPackage?.priceCent === pkg.priceCent;
        const isDisabled = minimumCredits !== undefined && pkg.amount < minimumCredits;
        const pricePerCredit = pkg.priceCent / pkg.amount;
        const taskEstimate = getCreditsTaskEstimate(pkg.amount);
        return (
          <XStack
            alignItems="center"
            aria-checked={isSelected}
            aria-disabled={isDisabled}
            borderColor={isSelected && !isDisabled ? 'borderSecondary' : 'borderPrimary'}
            borderWidth={2}
            className={` h-full ${isDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:border-borderSecondary'}`}
            gapX="lg"
            key={pkg.amount}
            onClick={() => {
              if (!isDisabled) onSelectPackage(pkg);
            }}
            onKeyDown={(e) => {
              if (!isDisabled) handleKeyDown(e, pkg);
            }}
            px="lg"
            py="sm"
            radius="lg"
            role="radio"
            tabIndex={isDisabled ? -1 : 0}
          >
            <Icon name={isSelected ? 'IconCircleCheckFilled' : 'IconCircle'} size={24} />
            {showCredits ? (
              <XStack flex={1}>
                <Text type="h4">
                  {formatNumber(pkg.amount)} {pluralize(unitTerm, pkg.amount)}
                </Text>
              </XStack>
            ) : (
              <XStack alignItems="center" flex={1} gap="xs">
                {pkg.originalPriceCent !== undefined ? (
                  <Text color="tertiary" style={{ textDecoration: 'line-through' }} type="p-sm">
                    {formatCurrencyNatural(pkg.originalPriceCent)}
                  </Text>
                ) : null}
                <Text type="h2">{formatCurrencyNatural(pkg.priceCent)}</Text>
              </XStack>
            )}
            {isDisabled ? (
              <Text color="tertiary" type="p-sm">
                Not enough for this task
              </Text>
            ) : showCredits ? (
              <YStack alignItems="end" gapY="xs">
                <XStack alignItems="center" gap="xs">
                  {pkg.originalPriceCent !== undefined ? (
                    <Text color="tertiary" style={{ textDecoration: 'line-through' }} type="p-sm">
                      {formatCurrencyNatural(pkg.originalPriceCent)}
                    </Text>
                  ) : null}
                  <Text type="h2">{formatCurrencyNatural(pkg.priceCent)}</Text>
                </XStack>
                <Text className="text-blue-600" color="primary" type="p-sm">
                  {formatCurrencyNatural(makeCent(pricePerCredit))}/{unitTerm}
                </Text>
              </YStack>
            ) : (
              <YStack alignItems="end">
                <Text className="text-blue-600" color="primary" type="p-sm">
                  {taskEstimate.min === taskEstimate.max
                    ? `~${taskEstimate.min} task`
                    : `~${taskEstimate.min}-${taskEstimate.max} tasks`}
                </Text>
              </YStack>
            )}
          </XStack>
        );
      })}
    </YStack>
  );
};
